'use client';

import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { SuggestedSudsProperties } from '@/components/markers/SuggestedSudsDrawer/SuggestedSudsDrawer';

type SuggestedSudsFeature = {
  properties: SuggestedSudsProperties;
};

type SuggestedSudsContextType = {
  isSuggestedSudsDrawerOpen: boolean;
  suggestedSudProperties: any;
  suggestedSudsData: any;
  focusedSuggestedSudsId: number | null;
  openSuggestedSudsDrawer: () => void;
  closeSuggestedSudsDrawer: () => void;
  setFocusedSuggestedSudsId: React.Dispatch<React.SetStateAction<number | null>>;
  setSuggestedSudsData: React.Dispatch<React.SetStateAction<any>>;
  clearDrawer: () => void;
};

export const SuggestedSudsContext = createContext<SuggestedSudsContextType | undefined>(undefined);

type SuggestedSudsContextProviderProps = {
  children: ReactNode;
};

export function SuggestedSudsContextProvider({ children }: SuggestedSudsContextProviderProps) {
  const [
    isSuggestedSudsDrawerOpen,
    { open: openSuggestedSudsDrawer, close: closeSuggestedSudsDrawer },
  ] = useDisclosure(false);

  const [suggestedSudsData, setSuggestedSudsData] = useState<any>(null);
  const [focusedSuggestedSudsId, setFocusedSuggestedSudsId] = useState<number | null>(null);

  const suggestedSudProperties = useMemo(() => {
    if (!focusedSuggestedSudsId) {
      return null;
    }

    const feature = suggestedSudsData.features.find(
      (feature: SuggestedSudsFeature) => feature.properties.fid === focusedSuggestedSudsId
    );
    if (!feature) {
      return null;
    }

    return feature.properties;
  }, [suggestedSudsData, focusedSuggestedSudsId]);

  const clearDrawer = () => {
    closeSuggestedSudsDrawer();
    setFocusedSuggestedSudsId(null);
  };

  return (
    <SuggestedSudsContext.Provider
      value={{
        isSuggestedSudsDrawerOpen,
        suggestedSudProperties,
        suggestedSudsData,
        focusedSuggestedSudsId,
        openSuggestedSudsDrawer,
        closeSuggestedSudsDrawer,
        setFocusedSuggestedSudsId,
        setSuggestedSudsData,
        clearDrawer,
      }}
    >
      {children}
    </SuggestedSudsContext.Provider>
  );
}

export const useSuggestedSudsContext = () => {
  const context = useContext(SuggestedSudsContext);
  if (context === undefined) {
    throw new Error('useSuggestedSudsContext must be used within a SuggestedSudsContextProvider');
  }
  return context;
};
